import { withHydrate } from '@relay/react/hooks';
import ApiService from '@core/api/services/ApiService';
import UrlResolver from '@core/url-resolver/UrlResolver';
import { gtm } from '@helpers/dataLayer';
import getDocumentName from '@helpers/getDocumentName';
import { getFormFieldsTheme } from '@helpers/getFormFieldsTheme';
import { Device, from } from '@helpers/media';
import { useBodyScrollLockNew } from '@hooks/useBodyScrollLock';
import IconX from '@img/icons/icon-x.svg';
import Button from '@stories/Elements/Forms/Buttons/Button';
import { CheckboxInput, CheckboxLabel } from '@stories/Elements/Forms/Fields/Checkbox/Checkbox.styles';
import { ErrorText, Field, SplitField } from '@stories/Elements/Forms/Fields/Form.styles';
import SelectUnderline from '@stories/Elements/Forms/Fields/Select/SelectUnderline';
import { LabelUnderline, TextboxUnderlineInput } from '@stories/Elements/Forms/Fields/Textbox/Textbox.styles';
import Image from '@stories/Elements/Media/Image/Image';
import Loader from '@stories/Elements/Misc/Loader/Loader.hybrid';
import { Grid } from '@stories/Settings/Grids/Grid.styles';
import { AnimatePresence } from 'framer-motion';
import * as React from 'react';
import S from './LasersPopupForm.styles';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const LasersPopupForm = props => {
  // Form fields
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [userTypeValue, setUserTypeValue] = React.useState('');
  const [companyName, setCompanyName] = React.useState('');
  const [employeeNumberValue, setEmployeeNumberValue] = React.useState('');
  const [tradeValue, setTradeValue] = React.useState('');
  const [marketing, setMarketing] = React.useState(false);
  const [cultureValue, setCultureValue] = React.useState('');
  const [hasSentGTMEvent, setHasSentGTMEvent] = React.useState(false);

  // Utilities
  const [errors, setErrors] = React.useState();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  const theme = getFormFieldsTheme(props.formTheme);
  const hasImage = !!props.imageUrl;
  useBodyScrollLockNew(props.isOpen);
  const handleSubmit = async () => {
    const [item_category, item_category2, item_category3, item_category4, item_category5] = props.categories ?? [];
    gtm({
      event: 'request_demo',
      item_name: props.productCode || '',
      item_variant: props.productVariant?.name || '',
      item_category,
      item_category2,
      item_category3,
      item_category4,
      item_category5,
      item_id: props.productVariant?.agilityId?.toString() || ''
    });
    setIsSubmitting(true);
    const params = {
      formName: props.formName,
      firstName,
      lastName,
      email,
      trade: props.tradeOptions.find(x => x.key === tradeValue)?.key || '',
      relationship: userTypeValue || '',
      companyName,
      employees: employeeNumberValue || '',
      marketing,
      cultureCode: cultureValue !== undefined && cultureValue?.length > 0 ? cultureValue : props.pageContext.documentCulture,
      signupIdentifier: props.signupIdentifier,
      productCode: props.productCode,
      popupConfiguration: props.popupConfiguration
    };
    try {
      const apiResponse = await ApiService.request({
        baseUrl: UrlResolver.getDomain(),
        controller: 'lasers-popup-form',
        slug: 'submit',
        method: 'POST',
        params
      });
      setIsSubmitting(false);
      setErrors(apiResponse.errors);
      if (apiResponse.success) {
        gtm({
          event: 'form_submit',
          form_id: props.signupIdentifier || '',
          form_name: getDocumentName() ?? ''
        });
        setHasSubmitted(true);
      }
    } catch {
      setIsSubmitting(false);
    }
  };
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(AnimatePresence, {
      children: props.isOpen && /*#__PURE__*/_jsx(S.OuterContainer, {
        "data-form-theme": theme.global,
        onClick: props.onClose,
        children: /*#__PURE__*/_jsx(Grid.Default, {
          children: /*#__PURE__*/_jsxs(S.Container, {
            onClick: e => {
              e.stopPropagation();
              if (!hasSentGTMEvent) {
                gtm({
                  event: 'form_start',
                  form_id: props.signupIdentifier || '',
                  form_name: getDocumentName() ?? ''
                });
                setHasSentGTMEvent(true);
              }
            },
            children: [/*#__PURE__*/_jsxs(S.Split, {
              showGrid: hasImage,
              children: [hasImage && /*#__PURE__*/_jsx(S.ImageWrapper, {
                children: /*#__PURE__*/_jsx(Image, {
                  src: props.imageUrl ?? '',
                  sizes: `${from(Device.Tablet)} 50vw, 100vw`
                })
              }), /*#__PURE__*/_jsxs(S.FormWrapper, {
                children: [/*#__PURE__*/_jsxs(AnimatePresence, {
                  children: [isSubmitting && /*#__PURE__*/_jsx(S.Overlay, {
                    children: /*#__PURE__*/_jsx(Loader, {
                      theme: "white"
                    })
                  }, "overlay--submitting"), hasSubmitted && /*#__PURE__*/_jsx(S.Overlay, {
                    children: /*#__PURE__*/_jsxs(S.OverlayContent, {
                      children: [props.translations['custom.milwaukee.form.demo.confirmation.description'] && /*#__PURE__*/_jsx(S.OverlayTitle, {
                        children: props.translations['custom.milwaukee.form.demo.confirmation.description']
                      }), /*#__PURE__*/_jsx(S.OverlayMessage, {
                        dangerouslySetInnerHTML: {
                          __html: props.translations['custom.milwaukee.form.demo.confirmation.title']
                        }
                      }), /*#__PURE__*/_jsx(Button, {
                        text: props.translations['custom.milwaukee.form.demo.confirmation.cta'],
                        theme: "black",
                        onClick: props.onClose
                      })]
                    })
                  }, "overlay--finished")]
                }), /*#__PURE__*/_jsxs(S.Form, {
                  children: [/*#__PURE__*/_jsxs(S.FormHeader, {
                    children: [/*#__PURE__*/_jsx(S.FormTitle, {
                      children: props.title
                    }), props.description && /*#__PURE__*/_jsx(S.FormDescription, {
                      children: props.description
                    })]
                  }), /*#__PURE__*/_jsxs(SplitField, {
                    children: [/*#__PURE__*/_jsxs(Field, {
                      children: [/*#__PURE__*/_jsx(TextboxUnderlineInput, {
                        theme: theme.textbox,
                        id: "lasersPopupForm.firstName",
                        placeholder: props.translations['custom.milwaukee.form.firstName.placeholder'],
                        value: firstName,
                        onChange: e => setFirstName(e.currentTarget.value)
                      }), /*#__PURE__*/_jsx(LabelUnderline, {
                        htmlFor: "lasersPopupForm.firstName",
                        theme: theme.textbox,
                        children: props.translations['custom.milwaukee.form.firstName.placeholder']
                      }), errors && errors.find(x => x.fieldName === 'FirstName') && /*#__PURE__*/_jsx(ErrorText, {
                        theme: 'white',
                        children: errors.find(x => x.fieldName === 'FirstName')?.message
                      })]
                    }), /*#__PURE__*/_jsxs(Field, {
                      children: [/*#__PURE__*/_jsx(TextboxUnderlineInput, {
                        theme: theme.textbox,
                        id: "lasersPopupForm.lastName",
                        placeholder: props.translations['custom.milwaukee.form.lastName.placeholder'],
                        value: lastName,
                        onChange: e => setLastName(e.currentTarget.value)
                      }), /*#__PURE__*/_jsx(LabelUnderline, {
                        htmlFor: "lasersPopupForm.lastName",
                        theme: theme.textbox,
                        children: props.translations['custom.milwaukee.form.lastName.placeholder']
                      }), errors && errors.find(x => x.fieldName === 'LastName') && /*#__PURE__*/_jsx(ErrorText, {
                        theme: 'white',
                        children: errors.find(x => x.fieldName === 'LastName')?.message
                      })]
                    })]
                  }), /*#__PURE__*/_jsx(SelectUnderline, {
                    titleOptions: props.tradeOptions,
                    value: tradeValue ?? '',
                    onChange: setTradeValue,
                    theme: theme.select,
                    labelTheme: theme.textbox,
                    titleErrorMessage: errors && errors.find(x => x.fieldName === 'Trade')?.message,
                    displayError: !!errors && !!errors.find(x => x.fieldName === 'Trade')
                  }), props.cultureOptions && props.cultureOptions.length > 0 && /*#__PURE__*/_jsx(SelectUnderline, {
                    titleOptions: props.cultureOptions,
                    value: cultureValue ?? '',
                    onChange: setCultureValue,
                    theme: theme.select,
                    labelTheme: theme.textbox,
                    titleErrorMessage: errors && errors.find(x => x.fieldName === 'CultureCode')?.message,
                    displayError: !!errors && !!errors.find(x => x.fieldName === 'CultureCode')
                  }), /*#__PURE__*/_jsxs(SplitField, {
                    children: [/*#__PURE__*/_jsxs(Field, {
                      children: [/*#__PURE__*/_jsx(TextboxUnderlineInput, {
                        theme: theme.textbox,
                        id: "lasersPopupForm.email",
                        placeholder: props.translations['custom.milwaukee.form.emailAddress.placeholder'],
                        value: email,
                        onChange: e => setEmail(e.currentTarget.value)
                      }), /*#__PURE__*/_jsx(LabelUnderline, {
                        htmlFor: "lasersPopupForm.email",
                        theme: theme.textbox,
                        children: props.translations['custom.milwaukee.form.emailAddress.placeholder']
                      }), errors && errors.find(x => x.fieldName === 'Email') && /*#__PURE__*/_jsx(ErrorText, {
                        theme: 'white',
                        children: errors.find(x => x.fieldName === 'Email')?.message
                      })]
                    }), /*#__PURE__*/_jsx(SelectUnderline, {
                      titleOptions: props.userTypeOptions,
                      value: userTypeValue ?? '',
                      onChange: setUserTypeValue,
                      theme: theme.select,
                      labelTheme: theme.textbox,
                      titleErrorMessage: errors && errors.find(x => x.fieldName === 'Relationship')?.message,
                      displayError: !!errors && !!errors.find(x => x.fieldName === 'Relationship')
                    })]
                  }), /*#__PURE__*/_jsxs(SplitField, {
                    children: [/*#__PURE__*/_jsxs(Field, {
                      children: [/*#__PURE__*/_jsx(TextboxUnderlineInput, {
                        theme: theme.textbox,
                        id: "lasersPopupForm.companyName",
                        placeholder: props.translations['custom.milwaukee.form.companyName.placeholder'],
                        value: companyName,
                        onChange: e => setCompanyName(e.currentTarget.value)
                      }), /*#__PURE__*/_jsx(LabelUnderline, {
                        htmlFor: "lasersPopupForm.companyName",
                        theme: theme.textbox,
                        children: props.translations['custom.milwaukee.form.companyName.placeholder']
                      }), errors && errors.find(x => x.fieldName === 'CompanyName') && /*#__PURE__*/_jsx(ErrorText, {
                        theme: 'white',
                        children: errors.find(x => x.fieldName === 'CompanyName')?.message
                      })]
                    }), /*#__PURE__*/_jsx(SelectUnderline, {
                      titleOptions: props.employeeNumberOptions,
                      value: employeeNumberValue ?? '',
                      onChange: setEmployeeNumberValue,
                      theme: theme.select,
                      labelTheme: theme.textbox,
                      titleErrorMessage: errors && errors.find(x => x.fieldName === 'Employees')?.message,
                      displayError: !!errors && !!errors.find(x => x.fieldName === 'Employees')
                    })]
                  }), /*#__PURE__*/_jsxs(Field, {
                    children: [/*#__PURE__*/_jsx(CheckboxInput, {
                      theme: theme.checkbox,
                      id: "lasersPopupForm.marketing",
                      checked: marketing,
                      onChange: () => setMarketing(!marketing)
                    }), /*#__PURE__*/_jsx(CheckboxLabel, {
                      theme: theme.checkbox,
                      htmlFor: "lasersPopupForm.marketing",
                      dangerouslySetInnerHTML: {
                        __html: props.translations['custom.milwaukee.form.marketing.placeholder']
                      }
                    })]
                  }), /*#__PURE__*/_jsx(S.SubmitButtonWrapper, {
                    children: /*#__PURE__*/_jsx(Button, {
                      text: props.translations['custom.milwaukee.form.demo.cta'],
                      theme: theme.button,
                      onClick: handleSubmit,
                      disabled: isSubmitting || hasSubmitted
                    })
                  })]
                })]
              })]
            }), /*#__PURE__*/_jsx(S.CloseButton, {
              onClick: props.onClose,
              children: /*#__PURE__*/_jsx(IconX, {})
            })]
          })
        })
      })
    })
  });
};
export default withHydrate(LasersPopupForm, 'LasersPopupForm', 'hybrid');
